import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

class SybilSellingPlan extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.addEventListener('click', this.handleOptionClick, { signal });
    this.addEventListener('keydown', this.handleOptionKeydown, { signal });
    this.closest('.shopify-section, dialog')?.addEventListener(
      StandardEvents.productSelect,
      this.handleProductSelect,
      { signal },
    );
    this.coordinateInstances();
    if (this.isSubmissionOwner) this.applySelection(this.visualSelectedPlanId);
  }

  disconnectedCallback() {
    const formId = this.dataset.formId;
    this.abortController?.abort();
    this.abortController = undefined;
    queueMicrotask(() => SybilSellingPlan.coordinateForm(formId));
  }

  get control() {
    return this.querySelector('[data-sybil-selling-plan-control]');
  }

  get selectedPlanId() {
    if (!this.control || this.control.disabled) return '';
    return this.control.value;
  }

  get visualSelectedPlanId() {
    return (
      this.querySelector('[aria-checked="true"][data-sybil-purchase-option]')?.dataset
        .sellingPlanId ?? ''
    );
  }

  handleOptionClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const option = event.target.closest('[data-sybil-purchase-option]');
    if (
      !(option instanceof HTMLButtonElement) ||
      !this.contains(option) ||
      option.disabled ||
      !this.isSubmissionOwner
    ) {
      return;
    }
    this.applySelection(option.dataset.sellingPlanId ?? '');
  };

  handleOptionKeydown = (event) => {
    if (
      !(event.target instanceof HTMLButtonElement) ||
      !event.target.matches('[data-sybil-purchase-option]') ||
      !this.isSubmissionOwner
    ) {
      return;
    }

    const options = [...this.querySelectorAll('[data-sybil-purchase-option]')].filter(
      (option) => !option.disabled,
    );
    const currentIndex = options.indexOf(event.target);
    if (currentIndex < 0) return;

    let nextIndex;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % options.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + options.length) % options.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = options.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const nextOption = options[nextIndex];
    this.applySelection(nextOption.dataset.sellingPlanId ?? '');
    nextOption.focus();
  };

  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const selectedPlanId = this.visualSelectedPlanId;
    const generation = (this.pendingGeneration ?? 0) + 1;
    this.pendingGeneration = generation;
    if (this.isSubmissionOwner) this.setPurchasePending(true);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.newProduct) {
          this.dataset.productId = String(detail.newProduct.id);
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        const blockId = CSS.escape(this.dataset.blockId ?? '');
        const formId = CSS.escape(this.dataset.formId ?? '');
        const replacement =
          detail.html.querySelector(`sybil-selling-plan[data-block-id="${blockId}"]`) ??
          detail.html.querySelector(`sybil-selling-plan[data-form-id="${formId}"]`);
        const currentOptions = this.querySelector('[data-sybil-plan-options]');
        const replacementOptions = replacement?.querySelector('[data-sybil-plan-options]');
        if (!currentOptions || !replacementOptions) return;

        morph(currentOptions, replacementOptions, { childrenOnly: true });
        this.dataset.productId =
          String(detail.newProduct?.id ?? replacement.dataset.productId ?? this.dataset.productId);
        this.dataset.variantId = replacement.dataset.variantId ?? '';
        this.applySelection(selectedPlanId);
        this.coordinateInstances();
        queueMicrotask(() => this.syncStickyPrice());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[sybil-selling-plan] Product selection refresh failed:', error);
        }
      })
      .finally(() => {
        if (generation === this.pendingGeneration && this.isSubmissionOwner) {
          this.setPurchasePending(false);
        }
      });
  };

  applySelection(preferredPlanId = '') {
    const options = [...this.querySelectorAll('[data-sybil-purchase-option]')];
    if (options.length === 0 || !this.control) return;

    let selected = options.find(
      (option) =>
        !option.disabled &&
        preferredPlanId !== '' &&
        option.dataset.sellingPlanId === preferredPlanId,
    );
    selected ??= options.find(
      (option) => !option.disabled && option.dataset.purchaseOption === 'one-time',
    );
    selected ??= options.find((option) => !option.disabled);
    if (!selected) return;

    const sellingPlanId = selected.dataset.sellingPlanId ?? '';
    for (const option of options) {
      const isSelected = option === selected;
      option.setAttribute('aria-checked', String(isSelected));
      option.tabIndex = isSelected ? 0 : -1;
      option.classList.toggle('is-selected', isSelected);
    }

    this.control.value = sellingPlanId;
    this.control.disabled = !this.isSubmissionOwner || sellingPlanId === '';
    if (this.isSubmissionOwner) {
      this.control.name = 'selling_plan';
      this.control.setAttribute('form', this.dataset.formId ?? '');
    }
    this.control.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncSelectedPrice(selected);
    this.syncInstallments(sellingPlanId !== '');
  }

  coordinateInstances() {
    SybilSellingPlan.coordinateForm(this.dataset.formId);
  }

  static coordinateForm(formId) {
    if (!formId) return;
    const instances = [...document.querySelectorAll('sybil-selling-plan')].filter(
      (instance) => instance.isConnected && instance.dataset.formId === formId,
    );
    instances.forEach((instance, index) => instance.setSubmissionOwner(index === 0));
  }

  setSubmissionOwner(isOwner) {
    this.isSubmissionOwner = isOwner;
    this.toggleAttribute('data-sybil-duplicate', !isOwner);
    if (!isOwner) this.dataset.sybilDuplicate = 'true';
    else delete this.dataset.sybilDuplicate;

    const editorDuplicate = !isOwner && Boolean(window.Shopify?.designMode);
    this.hidden = !isOwner && !editorDuplicate;
    const note = this.querySelector('[data-sybil-duplicate-note]');
    if (note) note.hidden = !editorDuplicate;

    const selectedPlanId = this.visualSelectedPlanId;
    const options = this.querySelectorAll('[data-sybil-purchase-option]');
    options.forEach((option) => {
      option.disabled = !isOwner || option.dataset.available !== 'true';
    });

    if (!this.control) return;
    this.control.value = selectedPlanId;
    this.control.disabled = !isOwner || selectedPlanId === '';
    if (isOwner) {
      this.control.name = 'selling_plan';
      this.control.setAttribute('form', this.dataset.formId ?? '');
    } else {
      this.control.removeAttribute('name');
      this.control.removeAttribute('form');
    }
  }

  setPurchasePending(isPending) {
    if (isPending && this.purchasePending) return;
    if (!isPending && !this.purchasePending) return;
    this.purchasePending = isPending;
    if (isPending) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');
    const form = document.getElementById(this.dataset.formId ?? '')?.closest('product-form-component');
    const sticky = this.closest('.shopify-section')?.querySelector('sticky-add-to-cart');
    for (const owner of [form, sticky]) {
      if (!owner) continue;
      if (isPending) {
        owner.dataset.sybilPreviousAriaBusy = owner.getAttribute('aria-busy') ?? '';
        owner.setAttribute('aria-busy', 'true');
      } else {
        const previous = owner.dataset.sybilPreviousAriaBusy;
        if (previous) owner.setAttribute('aria-busy', previous);
        else owner.removeAttribute('aria-busy');
        delete owner.dataset.sybilPreviousAriaBusy;
      }
    }

    const buttons = [
      form?.querySelector('[ref="addToCartButton"]'),
      sticky?.querySelector('[ref="addToCartButton"]'),
    ].filter(Boolean);
    for (const button of buttons) {
      if (isPending) {
        button.dataset.sybilPendingWasDisabled = String(button.disabled);
        button.disabled = true;
      } else if (Object.hasOwn(button.dataset, 'sybilPendingWasDisabled')) {
        button.disabled = button.dataset.sybilPendingWasDisabled === 'true';
        delete button.dataset.sybilPendingWasDisabled;
      }
    }
  }

  syncSelectedPrice(option) {
    const formattedPrice = option.querySelector('[data-sybil-option-price]')?.textContent?.trim();
    const priceCents = option.dataset.priceCents ?? '';
    const selectedPrice = this.querySelector('[data-sybil-selected-price]');
    if (selectedPrice && formattedPrice) {
      selectedPrice.textContent = formattedPrice;
      selectedPrice.dataset.priceCents = priceCents;
    }
    this.syncStickyPrice(formattedPrice, priceCents);
  }

  syncStickyPrice(formattedPrice, priceCents) {
    const selected = this.querySelector('[aria-checked="true"][data-sybil-purchase-option]');
    const price = formattedPrice ?? selected?.querySelector('[data-sybil-option-price]')?.textContent?.trim();
    const cents = priceCents ?? selected?.dataset.priceCents ?? '';
    const stickyPrice = this.closest('.shopify-section')?.querySelector(
      'sticky-add-to-cart [data-testid="sticky-price-display"]',
    );
    if (!stickyPrice || !price) return;

    stickyPrice.textContent = price;
    stickyPrice.dataset.sybilSelectedPrice = 'true';
    stickyPrice.dataset.priceCents = cents;
  }

  syncInstallments(subscriptionSelected) {
    const installments = this.closest('.product-details')?.querySelectorAll(
      'product-price .installments',
    );
    installments?.forEach((installment) => {
      if (!Object.hasOwn(installment.dataset, 'sybilOriginalHidden')) {
        installment.dataset.sybilOriginalHidden = String(installment.hidden);
      }
      installment.hidden =
        subscriptionSelected || installment.dataset.sybilOriginalHidden === 'true';
    });
  }
}

if (!customElements.get('sybil-selling-plan')) {
  customElements.define('sybil-selling-plan', SybilSellingPlan);
}
